import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Calendar } from '@amir04lm26/react-modern-calendar-date-picker'
import '@amir04lm26/react-modern-calendar-date-picker/lib/DatePicker.css'
import { fieldBoxShadow } from './FormTextInput'
import CalendarSvg from '../other/svg/CalendarSvg'
import XMarkSvg from '../other/svg/XMarkSvg'

interface FormDateInputProps {
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  placeholder: string
  error?: boolean
}

interface DayValue {
  year: number
  month: number
  day: number
}

interface MenuPosition {
  top: number
  left: number
}

const parseDate = (dateString: string): DayValue | null => {
  if (dateString === '') {
    return null
  }
  const date = new Date(`${dateString}T00:00:00`)
  if (isNaN(date.getTime())) {
    return null
  }
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate()
  }
}

const formatIsoDate = (dayValue: DayValue | null): string => {
  if (dayValue === null) {
    return ''
  }
  const month = String(dayValue.month).padStart(2, '0')
  const day = String(dayValue.day).padStart(2, '0')
  return `${dayValue.year}-${month}-${day}`
}

const formatDisplayDate = (isoDate: string): string => {
  const parsed = parseDate(isoDate)
  if (parsed === null) {
    return ''
  }
  const month = String(parsed.month).padStart(2, '0')
  const day = String(parsed.day).padStart(2, '0')
  return `${day}.${month}.${parsed.year}`
}

const FormDateInput = ({ value, onChange, onBlur, placeholder, error = false }: FormDateInputProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false)
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null)
  const controlRef = useRef<HTMLDivElement>(null)
  const selectedDay = parseDate(value)
  const displayValue = formatDisplayDate(value)
  const isPlaceholder = displayValue.length === 0

  useLayoutEffect(() => {
    const updatePosition = (): void => {
      if (controlRef.current === null) {
        return
      }
      const rect = controlRef.current.getBoundingClientRect()
      const calendarHeight = 340
      const spaceBelow = window.innerHeight - rect.bottom
      const top =
        spaceBelow < calendarHeight && rect.top > calendarHeight ? rect.top - calendarHeight - 4 : rect.bottom + 4
      setMenuPosition({
        top,
        left: rect.left
      })
    }

    if (isOpen) {
      updatePosition()
      window.addEventListener('resize', updatePosition)
      window.addEventListener('scroll', updatePosition, true)
    } else {
      setMenuPosition(null)
    }

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [isOpen])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setIsOpen(false)
        if (typeof onBlur === 'function') {
          onBlur()
        }
      }
    }

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onBlur])

  return (
    <div className="self-stretch relative">
      <div
        ref={controlRef}
        className="self-stretch w-full h-11 bg-backgrounds-bg-field-component rounded-md inline-flex justify-center items-center overflow-hidden"
        style={{ boxShadow: fieldBoxShadow(error) }}>
        <button
          type="button"
          onClick={() => {
            setIsOpen((open) => !open)
          }}
          className="size-11 flex justify-center items-center shrink-0">
          <CalendarSvg className="size-5 text-foregrounds-fg-muted" />
        </button>
        <div className="w-px self-stretch bg-borders-border-base" />
        <button
          type="button"
          onClick={() => {
            setIsOpen((open) => !open)
          }}
          className={`flex-1 self-stretch px-4 flex items-center text-left text-sm font-medium font-Montserrat leading-5 ${
            isPlaceholder ? 'text-foregrounds-fg-muted' : 'text-foregrounds-fg-base font-semibold'
          }`}>
          {isPlaceholder ? placeholder : displayValue}
        </button>
        {!isPlaceholder && (
          <button
            type="button"
            aria-label="Clear"
            onClick={() => {
              onChange('')
              setIsOpen(false)
              if (typeof onBlur === 'function') {
                onBlur()
              }
            }}
            className="size-8 flex justify-center items-center shrink-0">
            <XMarkSvg className="size-[15px] text-buttons-text-secondary" />
          </button>
        )}
      </div>
      {isOpen &&
        menuPosition !== null &&
        createPortal(
          <>
            <div
              role="presentation"
              className="fixed inset-0 z-[100]"
              onClick={() => {
                setIsOpen(false)
                if (typeof onBlur === 'function') {
                  onBlur()
                }
              }}
            />
            <div
              className="fixed z-[200] rounded-lg bg-backgrounds-bg-field-component overflow-hidden"
              style={{
                top: menuPosition.top,
                left: menuPosition.left,
                boxShadow:
                  '0px 8px 16px 0px rgba(0,0,0,0.08), 0px 4px 8px 0px rgba(0,0,0,0.08), 0px 0px 0px 1px rgba(0,0,0,0.08)'
              }}>
              <Calendar
                value={selectedDay}
                onChange={(dayValue: DayValue | null) => {
                  onChange(formatIsoDate(dayValue))
                  setIsOpen(false)
                  if (typeof onBlur === 'function') {
                    onBlur()
                  }
                }}
                shouldHighlightWeekends
                colorPrimary="#3582CE"
                colorPrimaryLight="rgba(53, 130, 206, 0.1)"
              />
            </div>
          </>,
          document.body
        )}
    </div>
  )
}

export default FormDateInput
